import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import CurriculumPage from "./pages/CurriculumPage";
import ModulePage from "./pages/ModulePage";
import LessonChatPage from "./pages/LessonChatPage";
import MinnowRoom from "./pages/MinnowRoom";
import ChatRoom from "./pages/ChatRoom";
import SettingsPage from "./pages/SettingsPage";
import LabsPage from "./pages/LabsPage";
import LabPage from "./pages/LabPage";
import CertificationPage from "./pages/CertificationPage";
import PathwaysPage from "./pages/PathwaysPage";
import DemoPage from "./pages/DemoPage";
import RegionalRoomsPage from "./pages/RegionalRoomsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import BadgesPage from "./pages/BadgesPage";
import OfflineLessonsPage from "./pages/OfflineLessonsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Onboarding Check Route
const OnboardingCheck = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Admin Route (requires admin role)
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user && user.onboarding_complete) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with auth checking
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Onboarding (protected but allows incomplete onboarding) */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      
      {/* Protected Routes (require complete onboarding) */}
      <Route path="/dashboard" element={<OnboardingCheck><DashboardPage /></OnboardingCheck>} />
      <Route path="/curriculum" element={<OnboardingCheck><CurriculumPage /></OnboardingCheck>} />
      <Route path="/curriculum/:moduleId" element={<OnboardingCheck><ModulePage /></OnboardingCheck>} />
      <Route path="/curriculum/:moduleId/lesson/:lessonId" element={<OnboardingCheck><LessonChatPage /></OnboardingCheck>} />
      <Route path="/minnow" element={<OnboardingCheck><MinnowRoom /></OnboardingCheck>} />
      <Route path="/rooms/:roomType" element={<OnboardingCheck><ChatRoom /></OnboardingCheck>} />
      <Route path="/regional-rooms" element={<OnboardingCheck><RegionalRoomsPage /></OnboardingCheck>} />
      <Route path="/regional-rooms/:roomId" element={<OnboardingCheck><RegionalRoomsPage /></OnboardingCheck>} />
      <Route path="/settings" element={<OnboardingCheck><SettingsPage /></OnboardingCheck>} />
      <Route path="/labs" element={<OnboardingCheck><LabsPage /></OnboardingCheck>} />
      <Route path="/lab/:labId" element={<OnboardingCheck><LabPage /></OnboardingCheck>} />
      <Route path="/certification" element={<OnboardingCheck><CertificationPage /></OnboardingCheck>} />
      <Route path="/pathways" element={<OnboardingCheck><PathwaysPage /></OnboardingCheck>} />
      <Route path="/analytics" element={<OnboardingCheck><AnalyticsPage /></OnboardingCheck>} />
      <Route path="/weekly-plan" element={<OnboardingCheck><DashboardPage /></OnboardingCheck>} />
      <Route path="/badges" element={<OnboardingCheck><BadgesPage /></OnboardingCheck>} />
      <Route path="/offline-lessons" element={<OnboardingCheck><OfflineLessonsPage /></OnboardingCheck>} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* Admin Route (admin role only) */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
